import { useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { TopicCard } from "@/components/TopicCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { 
  Plus, 
  Search, 
  Grid3X3, 
  List, 
  Folder, 
  Filter,
  X,
  MessageCircle,
  FileText,
  Link2,
  Trash2
} from "lucide-react";
import { ThreadItem } from "@/components/ThreadItem";
import { ragService } from "@/services/ragService";

interface Topic {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  spaceId: string;
  tags?: string[];
  primarySpace?: { id: string; name: string; icon?: string; color?: string } | null;
  secondarySpaces?: Array<{ id: string; name: string; icon?: string; color?: string }>;
  secondarySpaceIds?: string[];
  threadCount?: number;
  sourceCount?: number;
  threads?: any[];
  sources?: any[];
  createdAt?: string;
  updatedAt?: string;
}

interface Space {
  id: string;
  name: string;
  icon?: string;
  color?: string;
}

export default function TopicsPage() {
  const { toast } = useToast();
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSpaceId, setSelectedSpaceId] = useState<string>("all");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showLinkSpaceModal, setShowLinkSpaceModal] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState<Topic | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    icon: "",
    primarySpaceId: "",
    secondarySpaceIds: [] as string[],
    tags: [] as string[],
  });

  // Fetch topics with filters
  const { data: topicsData, isLoading: topicsLoading } = useQuery<{
    topics: Topic[];
    total: number;
  }>({
    queryKey: ["/api/topics", { spaceId: selectedSpaceId !== "all" ? selectedSpaceId : undefined, search: searchQuery }],
  });

  // Fetch spaces for filter dropdown
  const { data: spaces = [] } = useQuery<Space[]>({
    queryKey: ["/api/mastermind-spaces"],
  });

  // Fetch single topic with full details - use template string for correct URL
  const topicDetailUrl = selectedTopic?.id ? `/api/topics/${selectedTopic.id}` : null;
  const { data: topicDetail, isLoading: detailLoading, refetch: refetchDetail } = useQuery<Topic>({
    queryKey: [topicDetailUrl],
    enabled: !!topicDetailUrl && showDetailModal,
  });

  const detailThreadIds = useMemo(
    () => (topicDetail?.threads ?? []).map((thread: { id: string }) => thread.id).filter(Boolean),
    [topicDetail?.threads]
  );
  const { data: threadEmbeddingStatusMap = {} } = useQuery({
    queryKey: ["thread-embedding-status", detailThreadIds.slice().sort().join(",")],
    queryFn: () => ragService.getThreadEmbeddingStatusForThreads(detailThreadIds),
    enabled: showDetailModal && detailThreadIds.length > 0,
    staleTime: 60_000,
  });

  // Create topic mutation
  const createTopicMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      return await apiRequest("/api/topics", {
        method: "POST",
        body: JSON.stringify(data),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/topics"] });
      setShowCreateModal(false);
      resetForm();
      toast({ title: "Topic created successfully" });
    },
    onError: (error: any) => {
      toast({ title: "Failed to create topic", description: error.message, variant: "destructive" });
    },
  });

  // Update topic mutation
  const updateTopicMutation = useMutation({
    mutationFn: async (data: typeof formData & { id: string }) => {
      return await apiRequest(`/api/topics/${data.id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/topics"] });
      setIsEditing(false);
      refetchDetail();
      toast({ title: "Topic updated successfully" });
    },
    onError: (error: any) => {
      toast({ title: "Failed to update topic", description: error.message, variant: "destructive" });
    },
  });

  // Delete topic mutation
  const deleteTopicMutation = useMutation({
    mutationFn: async (topicId: string) => {
      return await apiRequest(`/api/topics/${topicId}`, {
        method: "DELETE",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/topics"] });
      setShowDetailModal(false);
      setSelectedTopic(null);
      toast({ title: "Topic deleted successfully" });
    },
    onError: (error: any) => {
      toast({ title: "Failed to delete topic", description: error.message, variant: "destructive" });
    },
  });

  // Link space mutation
  const linkSpaceMutation = useMutation({
    mutationFn: async ({ topicId, spaceId }: { topicId: string; spaceId: string }) => {
      return await apiRequest(`/api/topics/${topicId}/link-space`, {
        method: "POST",
        body: JSON.stringify({ spaceId }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/topics"] });
      refetchDetail();
      setShowLinkSpaceModal(false);
      toast({ title: "Space linked successfully" });
    },
    onError: (error: any) => {
      toast({ title: "Failed to link space", description: error.message, variant: "destructive" });
    },
  });

  // Unlink space mutation
  const unlinkSpaceMutation = useMutation({
    mutationFn: async ({ topicId, spaceId }: { topicId: string; spaceId: string }) => {
      return await apiRequest(`/api/topics/${topicId}/unlink-space/${spaceId}`, {
        method: "DELETE",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/topics"] });
      refetchDetail();
      toast({ title: "Space unlinked successfully" });
    },
    onError: (error: any) => {
      toast({ title: "Failed to unlink space", description: error.message, variant: "destructive" });
    },
  });

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      icon: "",
      primarySpaceId: "",
      secondarySpaceIds: [],
      tags: [],
    });
  };

  const handleTopicClick = (topic: Topic) => {
    setSelectedTopic(topic);
    setShowDetailModal(true);
    setIsEditing(false);
  };

  const handleEditClick = (topic: Topic) => {
    setSelectedTopic(topic);
    setFormData({
      name: topic.name,
      description: topic.description || "",
      icon: topic.icon || "",
      primarySpaceId: topic.spaceId,
      secondarySpaceIds: topic.secondarySpaceIds || [],
      tags: topic.tags || [],
    });
    setShowDetailModal(true);
    setIsEditing(true);
  };

  const handleDeleteClick = (topic: Topic) => {
    if (confirm(`Are you sure you want to delete "${topic.name}"? This action cannot be undone.`)) {
      deleteTopicMutation.mutate(topic.id);
    }
  };

  const handleLinkSpaceClick = (topic: Topic) => {
    setSelectedTopic(topic);
    setShowLinkSpaceModal(true);
  };

  const handleCreateSubmit = () => {
    if (!formData.name || !formData.primarySpaceId) {
      toast({ title: "Name and Primary Space are required", variant: "destructive" });
      return;
    }
    createTopicMutation.mutate(formData);
  };

  const handleUpdateSubmit = () => {
    if (!selectedTopic || !formData.name || !formData.primarySpaceId) {
      toast({ title: "Name and Primary Space are required", variant: "destructive" });
      return;
    }
    updateTopicMutation.mutate({ ...formData, id: selectedTopic.id });
  };

  const availableSpacesForLinking = useMemo(() => {
    if (!selectedTopic) return [];
    const linkedIds = new Set([selectedTopic.spaceId, ...(selectedTopic.secondarySpaceIds || [])]);
    return spaces.filter(s => !linkedIds.has(s.id));
  }, [selectedTopic, spaces]);

  const topics = topicsData?.topics || [];

  return (
    <div className="flex h-[calc(100vh-4rem)] overflow-hidden" data-testid="page-topics">
      {/* Left Sidebar - Filters */}
      <div className="w-64 border-r p-4 flex flex-col gap-4 shrink-0">
        <h2 className="font-semibold text-lg flex items-center gap-2">
          <Filter className="h-5 w-5" /> Filters
        </h2>
        
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Search</Label>
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search topics..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8"
                data-testid="input-search-topics"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1 h-7 w-7"
                  onClick={() => setSearchQuery("")}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Space</Label>
            <Select value={selectedSpaceId} onValueChange={setSelectedSpaceId}>
              <SelectTrigger data-testid="select-space-filter">
                <SelectValue placeholder="All Spaces" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Spaces</SelectItem>
                {spaces.map((space) => (
                  <SelectItem key={space.id} value={space.id}>
                    {space.icon} {space.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-auto pt-4 border-t">
          <div className="text-sm text-muted-foreground">
            {topics.length} topic{topics.length !== 1 ? "s" : ""} found
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="border-b p-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Folder className="h-6 w-6" /> Topics
          </h1>
          <div className="flex items-center gap-2">
            <div className="flex border rounded-md">
              <Button
                variant={viewMode === "grid" ? "secondary" : "ghost"}
                size="icon"
                onClick={() => setViewMode("grid")}
                data-testid="button-view-grid"
              >
                <Grid3X3 className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === "list" ? "secondary" : "ghost"}
                size="icon"
                onClick={() => setViewMode("list")}
                data-testid="button-view-list"
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
            <Button onClick={() => { resetForm(); setShowCreateModal(true); }} data-testid="button-create-topic">
              <Plus className="h-4 w-4 mr-2" /> New Topic
            </Button>
          </div>
        </div>

        {/* Content Area */}
        <ScrollArea className="flex-1 p-4">
          {topicsLoading ? (
            <div className={viewMode === "grid" ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" : "space-y-1"}>
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Skeleton key={i} className={viewMode === "grid" ? "h-48" : "h-16"} />
              ))}
            </div>
          ) : topics.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <Folder className="h-16 w-16 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No topics found</h3>
              <p className="text-muted-foreground mb-4">
                {searchQuery || selectedSpaceId !== "all" 
                  ? "Try adjusting your filters" 
                  : "Create your first topic to get started"}
              </p>
              <Button onClick={() => { resetForm(); setShowCreateModal(true); }}>
                <Plus className="h-4 w-4 mr-2" /> Create Topic
              </Button>
            </div>
          ) : viewMode === "grid" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {topics.map((topic) => (
                <TopicCard
                  key={topic.id}
                  topic={topic}
                  viewMode="grid"
                  onClick={handleTopicClick}
                  onEdit={handleEditClick}
                  onDelete={handleDeleteClick}
                  onLinkSpace={handleLinkSpaceClick}
                />
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="p-0">
                {topics.map((topic) => (
                  <TopicCard
                    key={topic.id}
                    topic={topic}
                    viewMode="list"
                    onClick={handleTopicClick}
                    onEdit={handleEditClick}
                    onDelete={handleDeleteClick}
                    onLinkSpace={handleLinkSpaceClick}
                  />
                ))}
              </CardContent>
            </Card>
          )}
        </ScrollArea>
      </div>

      {/* Create Topic Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Topic</DialogTitle>
            <DialogDescription>Add a new topic to organize your knowledge</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                placeholder="Topic name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="input-topic-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Optional description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                data-testid="input-topic-description"
              />
            </div>
            <div className="space-y-2">
              <Label>Icon Name (optional)</Label>
              <Input
                placeholder="e.g., folder, book, star"
                value={formData.icon}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                data-testid="input-topic-icon"
              />
              <p className="text-xs text-muted-foreground">Enter a Lucide icon name (optional)</p>
            </div>
            <div className="space-y-2">
              <Label>Primary Space *</Label>
              <Select 
                value={formData.primarySpaceId} 
                onValueChange={(v) => setFormData({ ...formData, primarySpaceId: v })}
              >
                <SelectTrigger data-testid="select-primary-space">
                  <SelectValue placeholder="Select primary space" />
                </SelectTrigger>
                <SelectContent>
                  {spaces.map((space) => (
                    <SelectItem key={space.id} value={space.id}>
                      {space.icon} {space.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button 
              onClick={handleCreateSubmit} 
              disabled={createTopicMutation.isPending}
              data-testid="button-submit-create-topic"
            >
              {createTopicMutation.isPending ? "Creating..." : "Create Topic"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Topic Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {detailLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          ) : topicDetail && !isEditing ? (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <Folder className="h-8 w-8 text-muted-foreground" />
                    <div>
                      <DialogTitle className="text-xl">{topicDetail.name}</DialogTitle>
                      {topicDetail.primarySpace && (
                        <Badge variant="secondary" className="mt-1">
                          {topicDetail.primarySpace.icon} {topicDetail.primarySpace.name}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleEditClick(topicDetail)}>
                      Edit
                    </Button>
                    <Button 
                      variant="destructive" 
                      size="sm" 
                      onClick={() => handleDeleteClick(topicDetail)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </DialogHeader>
              
              {topicDetail.description && (
                <p className="text-muted-foreground">{topicDetail.description}</p>
              )}

              {topicDetail.secondarySpaces && topicDetail.secondarySpaces.length > 0 && (
                <div>
                  <Label className="text-sm">Linked Spaces</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {topicDetail.secondarySpaces.map((space) => (
                      <Badge key={space.id} variant="outline" className="pr-1">
                        {space.icon} {space.name}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-4 w-4 ml-1"
                          onClick={() => unlinkSpaceMutation.mutate({ topicId: topicDetail.id, spaceId: space.id })}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </Badge>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleLinkSpaceClick(topicDetail)}
                    >
                      <Link2 className="h-4 w-4 mr-1" /> Link Space
                    </Button>
                  </div>
                </div>
              )}

              <Tabs defaultValue="threads" className="mt-4">
                <TabsList>
                  <TabsTrigger value="threads">
                    <MessageCircle className="h-4 w-4 mr-1" /> Threads ({topicDetail.threadCount || 0})
                  </TabsTrigger>
                  <TabsTrigger value="sources">
                    <FileText className="h-4 w-4 mr-1" /> Sources ({topicDetail.sourceCount || 0})
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="threads" className="mt-4">
                  {topicDetail.threads && topicDetail.threads.length > 0 ? (
                    <div className="space-y-2">
                      {topicDetail.threads.map((thread: any) => (
                        <ThreadItem
                          key={thread.id}
                          thread={thread}
                          viewMode="list"
                          topicId={topicDetail.id}
                          embeddingStatusMap={threadEmbeddingStatusMap}
                          onRefresh={refetchDetail}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center text-muted-foreground py-8">
                      No threads linked to this topic yet
                    </div>
                  )}
                </TabsContent>
                <TabsContent value="sources" className="mt-4">
                  {topicDetail.sources && topicDetail.sources.length > 0 ? (
                    <div className="space-y-2">
                      {topicDetail.sources.map((source: any) => (
                        <Card key={source.id} className="p-3 hover-elevate cursor-pointer">
                          <div className="font-medium">{source.title}</div>
                          <div className="text-sm text-muted-foreground">{source.type}</div>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center text-muted-foreground py-8">
                      No sources linked to this topic yet
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </>
          ) : isEditing && selectedTopic ? (
            <>
              <DialogHeader>
                <DialogTitle>Edit Topic</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Icon</Label>
                  <Input
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Primary Space *</Label>
                  <Select 
                    value={formData.primarySpaceId} 
                    onValueChange={(v) => setFormData({ ...formData, primarySpaceId: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select primary space" />
                    </SelectTrigger>
                    <SelectContent>
                      {spaces.map((space) => (
                        <SelectItem key={space.id} value={space.id}>
                          {space.icon} {space.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsEditing(false)}>Cancel</Button>
                <Button 
                  onClick={handleUpdateSubmit} 
                  disabled={updateTopicMutation.isPending}
                >
                  {updateTopicMutation.isPending ? "Saving..." : "Save Changes"}
                </Button>
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Link Space Modal */}
      <Dialog open={showLinkSpaceModal} onOpenChange={setShowLinkSpaceModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Link to Space</DialogTitle>
            <DialogDescription>
              Add this topic to another space as a secondary link
            </DialogDescription>
          </DialogHeader>
          {availableSpacesForLinking.length === 0 ? (
            <div className="text-center text-muted-foreground py-4">
              This topic is already linked to all available spaces
            </div>
          ) : (
            <div className="space-y-2">
              {availableSpacesForLinking.map((space) => (
                <Card 
                  key={space.id} 
                  className="p-3 hover-elevate cursor-pointer"
                  onClick={() => selectedTopic && linkSpaceMutation.mutate({ topicId: selectedTopic.id, spaceId: space.id })}
                >
                  <div className="flex items-center gap-2">
                    <span>{space.icon}</span>
                    <span className="font-medium">{space.name}</span>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
